import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, ChevronRight, Radio, ClipboardList, Bell, Eye, Users, ShieldCheck } from "lucide-react";

/** Tour de onboarding em 3 passos, disparado no primeiro login após aprovação.
 * Roteiros priorizados:
 *  - Consultor: Modo Plantão → Fila de casos → Auditoria.
 *  - Clínica: Reservas na fila → Prévia do DIR → Ativar notificações.
 *  - Admin: KPI + heatmap → Aprovar candidaturas → Comunicação.
 * Fecha ao clicar em "Finalizar" ou X — persiste via POST /me/onboarding-seen.
 */
const STEPS = {
  consultor: [
    { icon: Radio, title: "1 · Ligue o Modo Plantão", body: "No topo do painel do consultor, ative o botão 'Plantão' para começar a receber casos em tempo real. Enquanto ligado, você aparece na fila do Admin como disponível." },
    { icon: ClipboardList, title: "2 · Assuma casos da fila", body: "Casos com risco alto aparecem primeiro. Clique em qualquer caso para abrir o dossiê, iniciar triagem, gerar o DIR (PDF) e enviar para uma unidade parceira." },
    { icon: ShieldCheck, title: "3 · Confirme seus plantões", body: "No card 'Próximos plantões', confirme presença com 1 clique. Isso reduz buracos de cobertura e você ganha streak no ranking público." },
  ],
  clinica: [
    { icon: ClipboardList, title: "1 · Reservas chegam automáticas", body: "Quando um consultor RAF Direct te reserva uma vaga, o caso aparece na sua fila com o DIR (Dossiê Inteligente RAF) anexado. Sem cadastro manual, sem re-digitação." },
    { icon: Eye, title: "2 · Pré-visualize o DIR antes de aceitar", body: "Clique em 'Prévia do DIR' para abrir o PDF direto no painel — sem baixar. Assim seu médico avalia rapidamente antes de aceitar/recusar." },
    { icon: Bell, title: "3 · Ative notificações push", body: "Clique em 'Ativar notificações' no topo do painel. Você será alertado no navegador toda vez que uma nova reserva chegar, sem precisar ficar recarregando." },
  ],
  admin: [
    { icon: Users, title: "1 · Aprove candidaturas na aba 'Candidaturas'", body: "Consultores e unidades cadastradas via /seja-consultor e /seja-parceiro esperam aprovação. Um clique em 'Aprovar' cria a conta e envia senha temporária por email." },
    { icon: Radio, title: "2 · Acompanhe o plantão ao vivo", body: "Na aba 'Ao vivo' você vê quais consultores estão de plantão agora. Pode convidar reservas em massa via WhatsApp em 1 clique quando faltar cobertura." },
    { icon: Bell, title: "3 · Monitore o heatmap KPI", body: "O card no topo mostra volume das últimas 24h e o heatmap 7×24 dos últimos 30 dias. Clique numa célula quente para investigar picos anômalos." },
  ],
};

export const OnboardingTour = () => {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.onboarding_seen) return;
    if (!STEPS[user.role]) return;
    // Não mostra se ainda precisa trocar senha — deixa o fluxo de senha ser primeiro.
    if (user.requires_password_change) return;
    setOpen(true);
  }, [user]);

  if (!open || !user || !STEPS[user.role]) return null;
  const steps = STEPS[user.role];
  const current = steps[step];
  const Icon = current.icon;
  const isLast = step === steps.length - 1;

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/me/onboarding-seen");
      if (refreshUser) await refreshUser();
    } catch { /* silent — best-effort */ }
    setOpen(false);
    setDismissing(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="onboarding-tour">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl">
        <div className="p-5 sm:p-6 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Bem-vindo à RAF Direct</div>
            <div className="text-xs text-slate-500 mt-0.5">Passo {step + 1} de {steps.length}</div>
          </div>
          <button onClick={dismiss} data-testid="onboarding-close" aria-label="Fechar" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 sm:p-8 flex items-start gap-4">
          <div className="w-12 h-12 shrink-0 rounded-full bg-[#0B2B62]/10 text-[#0B2B62] flex items-center justify-center">
            <Icon className="w-6 h-6" strokeWidth={1.6} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-xl text-[#0B2B62]" data-testid="onboarding-title">{current.title}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{current.body}</p>
          </div>
        </div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-between gap-2 bg-slate-50">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <span key={i} className={`w-6 h-1.5 rounded-full ${i === step ? "bg-[#0B2B62]" : "bg-slate-300"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={dismiss} data-testid="onboarding-skip" disabled={dismissing}
              className="text-xs px-3 py-2 text-slate-500 hover:text-slate-800">Pular</button>
            {isLast ? (
              <button onClick={dismiss} data-testid="onboarding-finish" disabled={dismissing}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0B2B62] text-white text-sm rounded-md font-semibold hover:bg-[#0e3a82]">
                Vamos começar
              </button>
            ) : (
              <button onClick={() => setStep((s) => s + 1)} data-testid="onboarding-next"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0B2B62] text-white text-sm rounded-md font-semibold hover:bg-[#0e3a82]">
                Próximo <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
